import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { fadeInUp } from "@/motion/variants";
import { Button } from "@/components/ui/button";
import { ArrowUp } from "lucide-react";
import { useEffect, useState } from "react";

import { PADMORA_EIN, PADMORA_FORMATION_STATE, PADMORA_LEGAL_NAME } from "@/lib/constants";

const Terms = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Terms of Service | Legal Agreement | User Terms | Padmora"
        description="Comprehensive terms of service governing the use of Padmora's AI-powered trading platform, commodity services, and cloud solutions. Legal agreement for users and businesses."
        noindex={false}
        keywords={[
          "terms of service",
          "user agreement",
          "legal terms",
          "service conditions",
          "user rights",
          "liability terms",
          "service agreement",
          "platform terms",
          "legal compliance",
          "Wyoming law"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated legal compliance",
            "AI terms enforcement",
            "intelligent contract management",
            "machine learning legal monitoring"
          ],
          entities: [
            "Terms of Service",
            "Legal Agreement",
            "User Rights",
            "Wyoming Law",
            "Service Terms",
            "Platform Agreement"
          ],
          topics: [
            "Legal Terms",
            "Service Agreements",
            "User Rights",
            "Legal Compliance",
            "Contract Terms"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Terms of Service",
          "description": "Comprehensive terms of service governing the use of Padmora's services and platform.",
          "mainEntity": {
            "@type": "CreativeWork",
            "name": "Padmora Terms of Service",
            "description": "Official terms and conditions for using Padmora's services",
            "about": [
              {
                "@type": "Thing",
                "name": "Service Agreement",
                "description": "Legal agreement governing service usage"
              },
              {
                "@type": "Thing",
                "name": "User Terms",
                "description": "Terms and conditions for platform users"
              }
            ]
          },
          "publisher": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          }
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Terms of Service", url: "https://padmora.com/terms" }
        ]}
        canonical="https://padmora.com/terms"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="min-h-[60vh] flex items-center justify-center">
        {/* Content */}
        <div className="container mx-auto px-6 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-800 text-foreground mb-6 leading-tight">
              Terms of <span className="text-primary">Service</span>
            </h1>
            <p className="text-xl text-secondary font-500 mb-8 leading-relaxed max-w-2xl mx-auto">
              Legal framework for our managed hosting services and platform
            </p>
          </div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <div className="prose prose-slate max-w-none">
              {/* Who We Are */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">1. Who We Are</h2>
                <p className="text-secondary leading-relaxed">
                  These Terms of Service ("Terms") cover your access to and use of services offered by {PADMORA_LEGAL_NAME}
                  ({PADMORA_FORMATION_STATE}, USA, EIN: {PADMORA_EIN}) ("Padmora", "we", "our", or "us")
                  through our websites, apps, and products.
                </p>
              </div>

              {/* Acceptance */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">2. Acceptance</h2>
                <p className="text-secondary leading-relaxed">
                  By using our services, you agree to these Terms. If you don't agree, don't use Padmora.
                </p>
              </div>

              {/* Who Can Use */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">3. Who Can Use Padmora</h2>
                <p className="text-secondary leading-relaxed">
                  You must be at least 18 years old or have legal capacity in your country. If using on behalf of a company, 
                  you confirm you have authority.
                </p>
              </div>

              {/* Account Registration */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">4. Account Registration</h2>
                <p className="text-secondary leading-relaxed">
                  You agree to provide accurate info. You're responsible for your account and all activities under it.
                </p>
              </div>

              {/* Services Overview */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">5. Services Overview</h2>
                <p className="text-secondary leading-relaxed">
                  Padmora provides managed hosting services, tools, resources, automation, community access, and related services 
                  for founders, startups, and creators. Some services may require payment; 
                  you'll see the terms before any charge.
                </p>
              </div>

              {/* Infrastructure & Data Hosting */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">6. Infrastructure & Data Hosting</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  Our service is powered by secure and compliant cloud infrastructure managed by trusted third-party providers. 
                  Each project runs in an isolated environment under our management, and we handle all technical operations 
                  including maintenance, monitoring, and backups.
                </p>
                <p className="text-secondary leading-relaxed">
                  <strong className="text-foreground">Important:</strong> While we maintain high standards for service reliability, we are not liable for 
                  indirect losses caused by third-party infrastructure outages, maintenance, or other events beyond our direct control.
                </p>
              </div>

              {/* Data Ownership & Privacy */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">7. Data Ownership & Privacy</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  All data created or uploaded by you remains your property. We act solely as a processor and do not access 
                  or use your data except as necessary to deliver the service. You may request data export or deletion at any time.
                </p>
                <p className="text-secondary leading-relaxed">
                  We store and process your data only to provide the service, using secure, compliant infrastructure. 
                  See our Privacy Policy for detailed information about data handling.
                </p>
              </div>

              {/* Service Continuity */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">8. Service Continuity</h2>
                <p className="text-secondary leading-relaxed">
                  In case of service provider changes or infrastructure updates, we commit to migrating user projects 
                  without data loss. We maintain regular encrypted backups for disaster recovery purposes and will provide 
                  reasonable notice for any planned maintenance that may affect service availability.
                </p>
              </div>

              {/* Use Rules */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">9. Use Rules</h2>
                <ul className="list-disc pl-6 text-secondary space-y-2">
                  <li>No illegal, harmful, or abusive activity.</li>
                  <li>No reselling, hacking, or disrupting our services.</li>
                  <li>Follow all applicable laws and regulations.</li>
                  <li>No unauthorized access to other users' data or systems.</li>
                  <li>Comply with our fair use policies for shared resources.</li>
                </ul>
              </div>

              {/* Payments & Refunds */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">10. Payments & Refunds</h2>
                <p className="text-secondary leading-relaxed">
                  If you subscribe or purchase, you agree to the prices and billing terms shown. Refunds are handled on a 
                  case-by-case basis—reach out, and we'll try to help. All fees are non-refundable unless otherwise specified 
                  or required by law.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">11. Intellectual Property</h2>
                <p className="text-secondary leading-relaxed">
                  Content and materials on Padmora are owned by us or our licensors. Don't copy, sell, or distribute without permission. 
                  You retain ownership of your content while granting us necessary rights to provide the service.
                </p>
              </div>

              {/* User Content */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">12. User Content</h2>
                <p className="text-secondary leading-relaxed">
                  You own your content but give us rights to use, host, and display it as needed to operate our services. 
                  You're responsible for what you post and must ensure you have the right to use any content you upload.
                </p>
              </div>

              {/* Privacy */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">13. Privacy</h2>
                <p className="text-secondary leading-relaxed">
                  We collect analytics and info via cookies and forms, but never sell your data to third parties. {PADMORA_LEGAL_NAME}
                  may access user data for service delivery. See our Privacy Policy for details.
                </p>
              </div>

              {/* Disclaimers */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">14. Disclaimers</h2>
                <p className="text-secondary leading-relaxed mb-4">
                  Padmora is provided 'as is'. We do our best but can't guarantee uninterrupted or error-free service. 
                  We're not liable for indirect damages, lost profits, or consequential losses.
                </p>
                <p className="text-secondary leading-relaxed">
                  <strong className="text-foreground">Service Availability:</strong> We strive for high availability but do not guarantee 100% uptime. 
                  We are not liable for any damages resulting from service interruptions.
                </p>
              </div>

              {/* Governing Law */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">15. Governing Law</h2>
                <p className="text-secondary leading-relaxed">
                  These Terms are governed by the laws of {PADMORA_FORMATION_STATE}, USA. {PADMORA_LEGAL_NAME} is the responsible entity for all users.
                </p>
              </div>

              {/* Changes */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">16. Changes</h2>
                <p className="text-secondary leading-relaxed">
                  We may update these Terms. If significant, we'll notify you. Continued use means acceptance.
                </p>
              </div>

              {/* Contact */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <h2 className="text-2xl font-700 text-foreground mb-4">17. Contact</h2>
                <p className="text-secondary leading-relaxed">
                  Questions? Email michel@padmora.com.
                </p>
              </div>

              {/* Last Updated */}
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <p className="text-sm text-secondary">
                  <strong className="text-foreground">Last Updated:</strong> {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Back to Top Button */}
            {showBackToTop && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="fixed bottom-8 right-8 z-50"
              >
                <Button
                  onClick={scrollToTop}
                  size="sm"
                  className="rounded-full shadow-lg hover:shadow-xl transition-shadow"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Terms;
